import { useEffect, useRef, useState } from "react";
import { Input } from "@/components/ui/input";
import { MapPin } from "lucide-react";

const KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

function parseComponents(components, formatted) {
  const get = (type, short = false) => {
    const c = (components || []).find((x) => (x.types || []).includes(type));
    return c ? (short ? c.shortText : c.longText) || "" : "";
  };
  const line1 = [get("street_number"), get("route")].filter(Boolean).join(" ");
  return {
    line1,
    suburb: get("locality") || get("sublocality_level_1"),
    state: get("administrative_area_level_1", true),
    postcode: get("postal_code"),
    formatted: formatted || "",
  };
}

// Address autocomplete using Google Places API (New) REST — custom dropdown, AU-biased.
export const AddressAutocomplete = ({ value, onChange, onPlace, placeholder = "Start typing an address…", className = "", testid = "address-autocomplete" }) => {
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);
  const skipNextRef = useRef(false);

  useEffect(() => {
    if (!KEY || skipNextRef.current) { skipNextRef.current = false; return; }
    const q = (value || "").trim();
    if (q.length < 4) { setSuggestions([]); setOpen(false); return; }
    const t = setTimeout(async () => {
      try {
        const res = await fetch("https://places.googleapis.com/v1/places:autocomplete", {
          method: "POST",
          headers: { "Content-Type": "application/json", "X-Goog-Api-Key": KEY },
          body: JSON.stringify({ input: q, includedRegionCodes: ["au"] }),
        });
        const data = await res.json();
        const items = (data.suggestions || [])
          .map((s) => s.placePrediction)
          .filter(Boolean)
          .map((p) => ({ id: p.placeId, text: p.text?.text || "" }))
          .slice(0, 5);
        setSuggestions(items);
        setOpen(items.length > 0);
      } catch {
        setSuggestions([]);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [value]);

  useEffect(() => {
    const close = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const pick = async (s) => {
    setOpen(false);
    skipNextRef.current = true;
    onChange(s.text);
    try {
      const res = await fetch(`https://places.googleapis.com/v1/places/${s.id}`, {
        headers: { "X-Goog-Api-Key": KEY, "X-Goog-FieldMask": "addressComponents,formattedAddress" },
      });
      const data = await res.json();
      const parsed = parseComponents(data.addressComponents, data.formattedAddress);
      skipNextRef.current = true;
      onPlace?.(parsed);
    } catch {
      onPlace?.({ line1: s.text, suburb: "", state: "", postcode: "", formatted: s.text });
    }
  };

  return (
    <div ref={boxRef} className={`relative ${className}`}>
      <Input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        data-testid={testid}
        autoComplete="off"
        className="w-full"
      />
      {open && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-slate-200 rounded-sm shadow-lg overflow-hidden" data-testid={`${testid}-suggestions`}>
          {suggestions.map((s) => (
            <button key={s.id} type="button" onClick={() => pick(s)}
              className="w-full text-left px-3 py-2 text-sm text-slate-700 hover:bg-cyan-50 flex items-start gap-2 border-b border-slate-50 last:border-0"
              data-testid={`${testid}-suggestion`}>
              <MapPin size={13} className="text-cyan-600 mt-0.5 shrink-0" />
              <span className="truncate">{s.text}</span>
            </button>
          ))}
          <div className="px-3 py-1 text-[9px] text-slate-400 text-right">powered by Google</div>
        </div>
      )}
    </div>
  );
};

export default AddressAutocomplete;
